import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Globe, Users, Handshake, FolderKanban, Building2, RefreshCw, Pencil, Trash2 } from "lucide-react";
import api from "../api";
import { useAuth, can } from "../auth";
import { Badge, Spinner, Button, Input, Field, Modal, Textarea } from "../components/common";
import { formatMoney } from "../format";

const emptyForm = { name: "", industry: "", website: "", phone: "", email: "", address: "", size: "", notes: "" };

export default function CompanyDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writable = can.write(user);
  const [data, setData] = useState(null);
  const [refreshingLogo, setRefreshingLogo] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    api.get(`/companies/${id}/detail`).then((r) => setData(r.data)).catch(() => navigate("/companies"));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const refreshLogo = async () => {
    setRefreshingLogo(true);
    try {
      await api.post(`/companies/${id}/refresh-logo`);
      load();
    } finally {
      setRefreshingLogo(false);
    }
  };

  const openEdit = () => { setForm({ ...emptyForm, ...data.company }); setEditModal(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const saveEdit = async () => {
    await api.put(`/companies/${id}`, form);
    setEditModal(false); load();
  };
  const delCompany = async () => {
    if (!window.confirm(t("common.confirmDelete"))) return;
    await api.delete(`/companies/${id}`);
    navigate("/companies");
  };

  if (!data) return <Spinner />;
  const c = data.company;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/companies")} data-testid="detail-back" className="flex items-center gap-1 text-sm text-muted hover:text-txt transition-colors">
        <ArrowLeft size={16} /> {t("detail.back")}
      </button>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          {c.logo_url ? (
            <img src={c.logo_url} alt="" className="w-14 h-14 rounded-sm object-contain bg-white border border-border shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-sm bg-glow/15 text-glow flex items-center justify-center shrink-0"><Building2 size={24} /></div>
          )}
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{c.name}</h1>
            <div className="text-sm text-muted">{c.industry}{c.size && ` · ${c.size}`}</div>
            {c.website && (
              <div className="flex items-center gap-3 mt-1">
                <a href={`https://${c.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="text-sm text-primary flex items-center gap-1 hover:underline"><Globe size={13} />{c.website}</a>
                {writable && (
                  <button onClick={refreshLogo} disabled={refreshingLogo} data-testid="refresh-logo-btn"
                          className="text-xs text-muted hover:text-txt flex items-center gap-1 transition-colors disabled:opacity-50">
                    <RefreshCw size={12} className={refreshingLogo ? "animate-spin" : ""} /> {t("company.refreshLogo")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {writable && (
          <div className="flex gap-1 shrink-0">
            <button onClick={openEdit} data-testid="edit-company-btn" className="p-1.5 rounded-sm hover:bg-border text-muted transition-colors"><Pencil size={16} /></button>
            <button onClick={delCompany} data-testid="delete-company-btn" className="p-1.5 rounded-sm hover:bg-danger/15 text-muted hover:text-danger transition-colors"><Trash2 size={16} /></button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-border rounded-sm p-5">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Users size={16} className="text-primary" />{t("detail.contacts")} ({data.contacts.length})</h3>
          {data.contacts.length === 0 ? <p className="text-sm text-muted">{t("detail.noItems")}</p> : (
            <div className="space-y-2">
              {data.contacts.map((x) => (
                <Link to={`/contacts/${x.id}`} key={x.id} className="block text-sm hover:text-primary transition-colors truncate">{x.first_name} {x.last_name} <span className="text-muted">· {x.title}</span></Link>
              ))}
            </div>
          )}
        </div>
        <div className="border border-border rounded-sm p-5">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Handshake size={16} className="text-amber-500" />{t("detail.deals")} ({data.deals.length})</h3>
          {data.deals.length === 0 ? <p className="text-sm text-muted">{t("detail.noItems")}</p> : (
            <div className="space-y-2">
              {data.deals.map((x) => (
                <div key={x.id} className="flex items-center justify-between text-sm"><span className="truncate">{x.title}</span><span className="font-medium shrink-0">{formatMoney(x.value, x.currency)}</span></div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-border rounded-sm p-5">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><FolderKanban size={16} className="text-success" />{t("detail.projects")} ({data.projects.length})</h3>
          {data.projects.length === 0 ? <p className="text-sm text-muted">{t("detail.noItems")}</p> : (
            <div className="space-y-2">
              {data.projects.map((x) => (
                <Link to={`/projects/${x.id}`} key={x.id} className="flex items-center justify-between text-sm hover:text-primary transition-colors"><span className="truncate">{x.name}</span><Badge value={x.status} label={t(`statuses.${x.status}`)} /></Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal open={editModal} onClose={() => setEditModal(false)} title={t("company.editCompany")}
        footer={<>
          <Button variant="ghost" onClick={() => setEditModal(false)}>{t("common.cancel")}</Button>
          <Button onClick={saveEdit} data-testid="save-company-btn">{t("common.save")}</Button>
        </>}>
        <Field label={t("company.name")}><Input data-testid="company-name" value={form.name} onChange={set("name")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("company.industry")}><Input value={form.industry || ""} onChange={set("industry")} /></Field>
          <Field label={t("company.size")}><Input value={form.size || ""} onChange={set("size")} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("company.website")}><Input value={form.website || ""} onChange={set("website")} /></Field>
          <Field label={t("company.phone")}><Input value={form.phone || ""} onChange={set("phone")} /></Field>
        </div>
        <Field label={t("company.email")}><Input type="email" value={form.email || ""} onChange={set("email")} /></Field>
        <Field label={t("company.address")}><Input value={form.address || ""} onChange={set("address")} /></Field>
        <Field label={t("company.notes")}><Textarea rows={3} value={form.notes || ""} onChange={set("notes")} /></Field>
      </Modal>
    </div>
  );
}
